/**
 * Client Documents — one client's documents, organised by its File Groups.
 *
 * One workspace: a location row on top (a searchable selector for All
 * documents, Ungrouped and each group, one level directly under the client,
 * with the group's actions beside it), and under it that set's documents with
 * search, Kind and Status, and selection.
 *
 * Groups are metadata (FileGroup ↔ FileGroupFile ↔ File, many-to-many): a
 * document may be in several, so "Ungrouped" is "in none", and Move is an
 * add then a remove. The client's file list does not carry membership, so
 * every group's detail is read and the rest is worked out in
 * ../fileGroups/membership. `?group=<id>` / `?group=ungrouped` / none for
 * All documents; the old `?folder=` is rewritten once.
 */

import AddIcon from '@mui/icons-material/Add';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	IconButton,
	InputAdornment,
	Menu,
	MenuItem,
	Link as MuiLink,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { useAuth } from '../../provider/auth';
import { PageHeader } from '../../provider/pageTitle';
import { useToast } from '../../provider/toast';
import { useClientFilesQuery } from '../../queries/clients';
import {
	useCreateFileGroup,
	useDeleteFileGroup,
	useFileGroupDetails,
	useFileGroupsQuery,
	useRenameFileGroup,
} from '../../queries/fileGroups';
import { useClientQuery, useDeleteFileMutation } from '../../queries/intake';
import { keys } from '../../queries/keys';
import type { FileRecord } from '../../schemas';
import { ConfirmDialog, Empty, StatusBadge } from '../../shared/ui';
import {
	needsMapping,
	needsProcessAgain,
	needsProcessing,
} from '../../utils/files';
import { bytes, count, dateTime } from '../../utils/format';
import {
	RowActionsMenu,
	SelectionActions,
	downloadMany,
	useGroupActions,
} from '../fileGroups/DocumentActions';
import { AddFilesDialog, NameDialog } from '../fileGroups/GroupDialogs';
import { GroupSelector } from '../fileGroups/GroupSelector';
import {
	addableGroups,
	duplicateGroupName,
	keepVisible,
	membersByGroup,
	selectionFromParams,
	selectionKey,
	selectionSearch,
	ungroupedIds,
	type Selection,
} from '../fileGroups/membership';
import { downloadFile } from '../files/FileTable';
import { documentsCrumbs } from '../modules/flow';
import { useModule } from '../modules/useModule';
import { KINDS, KindLabel, kindLook, kindOf } from '../shared/badges';
import { EmptyState, ErrorState } from '../shared/states';
import {
	CountSelect,
	DataTable,
	useFullScreen,
	type Column,
} from '../shared/tables';
import { ProcessAgain } from './ProcessAgain';

/* A status's dot, in the colour its badge uses. */
const TONE_DOT: Record<string, string> = {
	good: 'var(--ok-solid)',
	warn: 'var(--warn-solid)',
	bad: 'var(--danger-solid)',
	busy: 'var(--info-solid)',
	neutral: 'var(--class-adjustments)',
};

const documents = (n: number) => `${count(n)} document${n === 1 ? '' : 's'}`;

type Dialog =
	| { kind: 'new' }
	| { kind: 'rename' }
	| { kind: 'delete' }
	| { kind: 'add' }
	| { kind: 'remove'; ids: string[] }
	| null;

/** The row's download, as an icon. */
function DownloadIcon({ file }: { file: FileRecord }) {
	const download = useMutation({ mutationFn: downloadFile });
	return (
		<Tooltip
			title={
				download.error instanceof Error
					? download.error.message
					: 'Download original'
			}
		>
			<span>
				<IconButton
					size="small"
					aria-label={`Download ${file.originalName}`}
					disabled={download.isPending}
					onClick={(event) => {
						event.stopPropagation();
						download.mutate(file.pkFileId);
					}}
				>
					<FileDownloadOutlinedIcon fontSize="small" />
				</IconButton>
			</span>
		</Tooltip>
	);
}

export default function ClientLibrary() {
	const { clientId = '' } = useParams();
	const navigate = useNavigate();
	const toast = useToast();
	const { permissions, vocabulary } = useAuth();
	const canProcess = Boolean(permissions?.mapFormats);
	const canGroup = Boolean(permissions?.accessIntake);
	const canUpload = Boolean(permissions?.uploadFiles);
	/* Admins only (`deleteData`), the same gate as the API. */
	const canDelete = Boolean(permissions?.deleteData);
	const [deleteTarget, setDeleteTarget] = useState<FileRecord | null>(null);
	const deleteFile = useDeleteFileMutation();

	const [params, setParams] = useSearchParams();
	const { module, link } = useModule();
	const groupParam = params.get('group');
	const folderParam = params.get('folder');
	const { selection, legacy } = useMemo(
		() => selectionFromParams(groupParam, folderParam),
		[groupParam, folderParam],
	);
	const selKey = selectionKey(selection);
	useEffect(() => {
		if (legacy)
			setParams(selectionSearch(selection, module), { replace: true });
	}, [legacy, selection, module, setParams]);

	const [filters, setFilters] = useState({ q: '', kind: '', status: '' });
	const [view, setView] = useState<'list' | 'grid'>('list');
	const [ticked, setTicked] = useState<Set<string>>(new Set());
	const [dialog, setDialog] = useState<Dialog>(null);
	const [downloading, setDownloading] = useState(false);
	// The remove dialog fades out after `dialog` is cleared; keep its ids for the title.
	const removeIds = useRef<string[]>([]);
	if (dialog?.kind === 'remove') removeIds.current = dialog.ids;
	const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
	const full = useFullScreen('Data files', { compact: true });

	/* A new set of documents starts with nothing ticked and no filters. */
	useEffect(() => {
		setTicked(new Set());
		setFilters({ q: '', kind: '', status: '' });
	}, [selKey]);

	const client = useClientQuery(clientId);
	const files = useClientFilesQuery(clientId, module);
	const groups = useFileGroupsQuery(clientId, module);
	const groupList = useMemo(
		() =>
			[...(groups.data ?? [])].sort((a, b) =>
				a.name.localeCompare(b.name),
			),
		[groups.data],
	);
	const details = useFileGroupDetails(
		groupList.map((group) => group.pkFileGroupId),
	);
	const members = useMemo(
		() => membersByGroup(details.details),
		[details.details],
	);
	const create = useCreateFileGroup(clientId, module);
	const rename = useRenameFileGroup(clientId);
	const remove = useDeleteFileGroup(clientId);
	const actions = useGroupActions(clientId, groupList);
	const queryClient = useQueryClient();
	/* Until the group list has loaded there is nothing to read, and "every group"
     is vacuously ready: membership is only known once both are in. */
	const membershipReady = groups.isSuccess && details.ready;
	const retryGroups = () => {
		void queryClient.invalidateQueries({
			queryKey: keys.fileGroups.forClientAnyModule(clientId),
		});
		for (const group of groupList)
			void queryClient.invalidateQueries({
				queryKey: keys.fileGroups.detail(group.pkFileGroupId),
			});
	};

	const all = useMemo(() => files.data ?? [], [files.data]);
	const ungrouped = useMemo(
		() =>
			ungroupedIds(
				all.map((file) => file.pkFileId),
				members,
			),
		[all, members],
	);
	const currentGroup =
		selection.kind === 'group'
			? (groupList.find(
					(group) => group.pkFileGroupId === selection.id,
				) ?? null)
			: null;
	const currentGroupId = currentGroup?.pkFileGroupId ?? null;
	const missingGroup =
		selection.kind === 'group' && groups.isSuccess && !currentGroup;

	/* Membership is only known once the needed detail has loaded. */
	const setReady =
		selection.kind === 'all'
			? true
			: selection.kind === 'ungrouped'
				? membershipReady
				: members.has(selection.id);
	const inSet = useMemo(() => {
		if (selection.kind === 'all') return all;
		if (selection.kind === 'ungrouped')
			return all.filter((file) => ungrouped.has(file.pkFileId));
		const ids = members.get(selection.id);
		return ids ? all.filter((file) => ids.has(file.pkFileId)) : [];
	}, [all, selection, ungrouped, members]);

	const rows = useMemo(() => {
		const needle = filters.q.trim().toLowerCase();
		return inSet.filter((file) => {
			if (filters.kind && kindOf(file) !== filters.kind) return false;
			if (filters.status && file.status !== filters.status) return false;
			if (
				needle &&
				!`${file.originalName} ${file.periodLabel ?? ''}`
					.toLowerCase()
					.includes(needle)
			)
				return false;
			return true;
		});
	}, [inSet, filters]);

	const chosen = keepVisible(
		ticked,
		rows.map((file) => file.pkFileId),
	);
	const open = (next: Selection) => setParams(selectionSearch(next, module));
	const openFile = (file: FileRecord) =>
		navigate(link(`/intake/clients/${clientId}/files/${file.pkFileId}`));
	const startNewGroup = () => {
		create.reset();
		setDialog({ kind: 'new' });
	};
	const uploadHref = link(
		`/intake/clients/${clientId}/upload${currentGroupId ? `?group=${currentGroupId}` : ''}`,
	);
	const totalBytes = inSet.reduce(
		(sum, file) => sum + (file.sizeBytes ?? 0),
		0,
	);

	const download = async (ids: string[]) => {
		if (downloading) return;
		setDownloading(true);
		try {
			const failed = await downloadMany(ids);
			if (failed > 0)
				toast(
					'danger',
					`${count(failed)} of ${count(ids.length)} downloads failed`,
				);
		} finally {
			setDownloading(false);
		}
	};

	if (client.error) {
		return (
			<ErrorState
				error={client.error}
				onRetry={client.refetch}
				hint={
					<>
						Every client is listed in{' '}
						<Link className="link" to="/clients">
							Clients
						</Link>
						.
					</>
				}
			/>
		);
	}

	const kindOptions = KINDS.map((item) => ({
		value: item.value,
		label: item.label,
		colour: item.colour,
		n: inSet.filter((file) => kindOf(file) === item.value).length,
	})).filter((item) => item.n > 0 || item.value !== 'UNKNOWN');
	const statusOptions = [...new Set(inSet.map((file) => file.status))]
		.map((value) => {
			const term = vocabulary?.fileStatus[value];
			return {
				value,
				label: term?.label ?? value,
				colour: TONE_DOT[term?.tone ?? 'neutral'] ?? TONE_DOT.neutral!,
				n: inSet.filter((file) => file.status === value).length,
			};
		})
		.sort((a, b) => b.n - a.n || a.label.localeCompare(b.label));
	const anyFilter = Boolean(filters.q || filters.kind || filters.status);

	/* With no groups at all there is nothing to add to, move to or remove from. */
	const hasGroups = groupList.length > 0;
	const rowMenu = (file: FileRecord) =>
		(canGroup && hasGroups) || canDelete ? (
			<RowActionsMenu
				showGroups={canGroup && hasGroups}
				onDelete={canDelete ? () => setDeleteTarget(file) : undefined}
				fileName={file.originalName}
				groups={addableGroups(groupList, members, [file.pkFileId])}
				currentGroupId={currentGroupId}
				busy={actions.busy || !membershipReady}
				onAdd={(to) => void actions.add([file.pkFileId], to)}
				onMove={(to) =>
					currentGroupId &&
					void actions.move([file.pkFileId], currentGroupId, to)
				}
				onRemove={() =>
					setDialog({ kind: 'remove', ids: [file.pkFileId] })
				}
			/>
		) : null;

	const allColumns: Array<Column<FileRecord>> = [
		{
			key: 'name',
			label: 'Name',
			width: 230,
			sortValue: (file) => file.originalName,
			render: (file) => (
				<Typography
					noWrap
					title={file.originalName}
					sx={{
						fontSize: 'inherit',
						color: 'var(--ink)',
						fontWeight: 500,
					}}
				>
					{file.originalName}
				</Typography>
			),
		},
		{
			key: 'kind',
			label: 'File Types',
			width: 110,
			sortValue: (file) => kindLook(file).label,
			render: (file) => <KindLabel file={file} />,
		},
		{
			key: 'period',
			label: 'Period',
			width: 110,
			sortValue: (file) => file.periodLabel ?? '',
			render: (file) => file.periodLabel ?? '-',
		},
		{
			key: 'rows',
			label: 'Rows',
			num: true,
			width: 50,
			sortValue: (file) => file.rowCount ?? -1,
			render: (file) =>
				file.rowCount !== null ? count(file.rowCount) : '—',
		},
		{
			key: 'size',
			label: 'Size',
			num: true,
			width: 40,
			sortValue: (file) => file.sizeBytes ?? 0,
			render: (file) => bytes(file.sizeBytes),
		},
		{
			key: 'createdAt',
			label: 'Created at',
			width: 100,
			sortValue: (file) => file.createdAt,
			render: (file) => (
				<span className="nowrap">{dateTime(file.createdAt)}</span>
			),
		},
		{
			key: 'createdBy',
			label: 'Created by',
			width: 140,
			sortValue: (file) => file.createdByName ?? '',
			render: (file) => file.createdByName ?? '—',
		},
		{
			key: 'updatedAt',
			label: 'Last updated at',
			width: 150,
			sortValue: (file) => file.lastEditedAt ?? '',
			render: (file) => (
				<span className="nowrap">{dateTime(file.lastEditedAt)}</span>
			),
		},
		{
			key: 'updatedBy',
			label: 'Last updated by',
			width: 140,
			sortValue: (file) => file.lastEditedByName ?? '',
			render: (file) => file.lastEditedByName ?? '—',
		},
		{
			key: 'status',
			label: 'Status',
			width: 120,
			sortValue: (file) => file.status,
			render: (file) => (
				<Box
					title={file.statusNote ?? undefined}
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 'var(--s2)',
						flexWrap: 'wrap',
					}}
				>
					<StatusBadge group="fileStatus" value={file.status} />
					{/* Mapping confirmed: rows still being prepared, so say so rather
              than offer Review rules, which would fail. */}
					{file.status === 'MAPPING_CONFIRMED' && (
						<Box
							component="span"
							sx={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: '6px',
								fontSize: 'var(--fs-small)',
								color: 'var(--ink-3)',
								whiteSpace: 'nowrap',
							}}
						>
							<CircularProgress
								size={11}
								thickness={6}
								color="inherit"
							/>
							Preparing rows…
						</Box>
					)}
					{canProcess && needsProcessAgain(file) && (
						<ProcessAgain file={file} />
					)}
					{canProcess &&
						file.status !== 'MAPPING_CONFIRMED' &&
						(needsMapping(file) || needsProcessing(file)) && (
							<MuiLink
								component={Link}
								to={link(
									`/intake/clients/${clientId}/files/${file.pkFileId}/${needsMapping(file) ? 'mapping' : 'review'}`,
								)}
								underline="hover"
								onClick={(event: React.MouseEvent) =>
									event.stopPropagation()
								}
								sx={{
									fontSize: 'var(--fs-small)',
									whiteSpace: 'nowrap',
								}}
							>
								{needsMapping(file)
									? 'Map columns'
									: 'Review rules'}
							</MuiLink>
						)}
				</Box>
			),
		},
		{
			key: 'actions',
			label: 'Actions',
			width: 96,
			render: (file) => (
				/* Left-aligned under the header; the small icon buttons' own padding
           is pulled back so the first icon lines up with "Actions". */
				<Box
					sx={{
						display: 'inline-flex',
						alignItems: 'center',
						gap: '2px',
						ml: '-5px',
					}}
				>
					<DownloadIcon file={file} />
					{rowMenu(file)}
				</Box>
			),
		},
	];
	/* Sales / Returns / Combined is an O2C declaration: an H2R file has no kind. */
	const columns = allColumns.filter(
		(column) => !(module === 'H2R' && column.key === 'kind'),
	);

	const title =
		selection.kind === 'all'
			? 'All data files'
			: selection.kind === 'ungrouped'
				? 'Ungrouped'
				: (currentGroup?.name ?? 'Group');

	const selectionBar =
		chosen.length > 0 ? (
			<SelectionActions
				count={chosen.length}
				groups={addableGroups(groupList, members, chosen)}
				currentGroupId={currentGroupId}
				canGroup={canGroup && hasGroups}
				busy={actions.busy || !membershipReady}
				downloading={downloading}
				onAdd={(to) =>
					void actions
						.add(chosen, to)
						.then((ok) => ok && setTicked(new Set()))
				}
				onMove={(to) =>
					currentGroupId &&
					void actions
						.move(chosen, currentGroupId, to)
						.then((ok) => ok && setTicked(new Set()))
				}
				onRemove={() => setDialog({ kind: 'remove', ids: chosen })}
				onDownload={() => void download(chosen)}
				onClear={() => setTicked(new Set())}
			/>
		) : undefined;

	const emptyPanel = () => {
		if (all.length === 0)
			return (
				<Empty
					icon={<FolderOpenOutlinedIcon />}
					title="No files uploaded yet"
					action={
						canUpload ? (
							<Button
								variant="contained"
								onClick={() => navigate(uploadHref)}
							>
								Upload Files
							</Button>
						) : undefined
					}
				>
					Upload {client.data?.name ?? 'the client'}'s{' '}
					{module === 'H2R'
						? 'employee master, payroll or attendance'
						: 'sales or returns register'}{' '}
					to start.
				</Empty>
			);
		if (selection.kind === 'ungrouped')
			return (
				<Empty
					icon={<FolderOpenOutlinedIcon />}
					title="Every Files is in a group"
				/>
			);
		return (
			<Empty
				icon={<FolderOpenOutlinedIcon />}
				title="No Files in this group"
				action={
					<Box
						sx={{
							display: 'flex',
							gap: 'var(--s2)',
							justifyContent: 'center',
						}}
					>
						{canGroup && (
							<Button
								variant="outlined"
								onClick={() => setDialog({ kind: 'add' })}
							>
								Add Files
							</Button>
						)}
						{canUpload && (
							<Button
								variant="contained"
								onClick={() => navigate(uploadHref)}
							>
								Upload Files
							</Button>
						)}
					</Box>
				}
			>
				Add Files the client already has, or upload new ones straight
				into this group.
			</Empty>
		);
	};

	const loadingSet = files.isPending || !setReady;

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 'var(--s4)',
				minWidth: 0,
			}}
		>
			<PageHeader
				/* Clients › {client} › {Module} › Documents: the client crumb goes
           to its module page, the module crumb to the hub. */
				breadcrumbs={documentsCrumbs(
					{ id: clientId, name: client.data?.name },
					module,
				)}
				title="Data files"
				subtitle={
					[
						client.data?.code,
						client.data?.gstin,
						files.data ? documents(all.length) : null,
					]
						.filter(Boolean)
						.join(' · ') || undefined
				}
				actions={
					canUpload ? (
						<Button
							variant="contained"
							startIcon={<AddIcon />}
							onClick={() => navigate(uploadHref)}
						>
							Upload New Files
						</Button>
					) : undefined
				}
			/>

			<>
				<ErrorState error={files.error} onRetry={files.refetch} />
				<ErrorState
					error={groups.error ?? details.error}
					onRetry={retryGroups}
				/>

				{/* The controls sit on the page, in line with the header; only the
          table (or the empty state) is a white surface. */}
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 'var(--s3)',
						minWidth: 0,
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 'var(--s2)',
							flexWrap: 'wrap',
						}}
					>
						<GroupSelector
							groups={groupList}
							selection={selection}
							allCount={files.data ? all.length : null}
							ungroupedCount={
								files.data && membershipReady
									? ungrouped.size
									: null
							}
							canEdit={canGroup}
							onSelect={open}
							onNewGroup={startNewGroup}
						/>
						<Box sx={{ flex: 1 }} />
						{canGroup && currentGroup && (
							<>
								<Button
									size="small"
									variant="outlined"
									startIcon={<AddIcon />}
									onClick={() => setDialog({ kind: 'add' })}
								>
									Add Files
								</Button>
								<Tooltip title="Group actions">
									<IconButton
										size="small"
										aria-label={`Actions for ${currentGroup.name}`}
										aria-haspopup="menu"
										onClick={(event) =>
											setMoreAnchor(event.currentTarget)
										}
									>
										<MoreHorizIcon fontSize="small" />
									</IconButton>
								</Tooltip>
								<Menu
									anchorEl={moreAnchor}
									open={Boolean(moreAnchor)}
									onClose={() => setMoreAnchor(null)}
								>
									<MenuItem
										onClick={() => {
											setMoreAnchor(null);
											rename.reset();
											setDialog({ kind: 'rename' });
										}}
									>
										Rename group…
									</MenuItem>
									<MenuItem
										onClick={() => {
											setMoreAnchor(null);
											remove.reset();
											setDialog({ kind: 'delete' });
										}}
										sx={{ color: 'var(--danger-ink)' }}
									>
										Delete group…
									</MenuItem>
								</Menu>
								<Box
									aria-hidden="true"
									sx={{
										width: '1px',
										height: 20,
										bgcolor: 'var(--line)',
										mx: 'var(--s1)',
									}}
								/>
							</>
						)}
						{/* Creating a group is always one click away, not only inside the
              group selector. */}
						{canGroup && (
							<Button
								size="small"
								variant="outlined"
								startIcon={<CreateNewFolderOutlinedIcon />}
								onClick={startNewGroup}
							>
								New group
							</Button>
						)}
					</Box>

					{missingGroup ? (
						<Box>
							<EmptyState
								icon={<FolderOpenOutlinedIcon />}
								title="This group no longer exists"
								action={
									<Button
										variant="outlined"
										onClick={() => open({ kind: 'all' })}
									>
										Show all Files
									</Button>
								}
							>
								It may have been deleted or renamed by someone
								else.
							</EmptyState>
						</Box>
					) : (
						<>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									flexWrap: 'wrap',
									gap: 'var(--s2)',
								}}
							>
								<Box
									sx={{
										flex: '1 1 520px',
										display: 'flex',
										alignItems: 'center',
										flexWrap: 'wrap',
										gap: 'var(--s2)',
										minWidth: 0,
									}}
								>
									{selectionBar ?? (
										<>
											<TextField
												size="small"
												type="search"
												value={filters.q}
												placeholder="Search name or period"
												onChange={(event) =>
													setFilters((current) => ({
														...current,
														q: event.target.value,
													}))
												}
												slotProps={{
													input: {
														startAdornment: (
															<InputAdornment position="start">
																<SearchIcon fontSize="small" />
															</InputAdornment>
														),
													},
													htmlInput: {
														'aria-label':
															'Search data files',
													},
												}}
												sx={{
													flex: '1 1 240px',
													maxWidth: 400,
													'& .MuiOutlinedInput-root':
														{
															height: 'var(--ctl-h-sm)',
														},
												}}
											/>
											{module !== 'H2R' && (
												<CountSelect
													id="f-kind"
													label="Kind"
													size="small"
													inlineLabel
													value={filters.kind}
													onChange={(next) =>
														setFilters(
															(current) => ({
																...current,
																kind: next,
															}),
														)
													}
													allLabel="All kinds"
													total={inSet.length}
													options={kindOptions}
												/>
											)}
											<CountSelect
												id="f-status"
												label="Status"
												size="small"
												inlineLabel
												value={filters.status}
												onChange={(next) =>
													setFilters((current) => ({
														...current,
														status: next,
													}))
												}
												allLabel="All statuses"
												total={inSet.length}
												options={statusOptions}
											/>
										</>
									)}
								</Box>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 'var(--s1)',
										marginLeft: 'auto',
									}}
								>
									<Typography
										component="span"
										sx={{
											fontSize: 'var(--fs-small)',
											color: 'var(--ink-3)',
											whiteSpace: 'nowrap',
											mr: 'var(--s1)',
										}}
									>
										{loadingSet
											? 'Loading…'
											: anyFilter &&
												  rows.length !== inSet.length
												? `${count(rows.length)} of ${count(inSet.length)} shown`
												: `${documents(inSet.length)} · ${bytes(totalBytes)}`}
									</Typography>
									{anyFilter && (
										<Button
											size="small"
											variant="text"
											onClick={() =>
												setFilters({
													q: '',
													kind: '',
													status: '',
												})
											}
										>
											Clear filters
										</Button>
									)}
									<Box
										aria-hidden="true"
										sx={{
											width: '1px',
											height: 20,
											bgcolor: 'var(--line)',
											mx: 'var(--s1)',
										}}
									/>
									<ToggleButtonGroup
										exclusive
										size="small"
										value={view}
										aria-label="View"
										onChange={(
											_event,
											next: 'list' | 'grid' | null,
										) => next && setView(next)}
										sx={{
											'& .MuiToggleButton-root': {
												p: '4px',
											},
										}}
									>
										<ToggleButton
											value="list"
											aria-label="List"
										>
											<ViewListOutlinedIcon fontSize="small" />
										</ToggleButton>
										<ToggleButton
											value="grid"
											aria-label="Grid"
										>
											<GridViewOutlinedIcon fontSize="small" />
										</ToggleButton>
									</ToggleButtonGroup>
									{view === 'list' && full.button}
								</Box>
							</Box>

							{!loadingSet && inSet.length === 0 ? (
								<Box
									sx={{
										py: 'var(--s6)',
										bgcolor: 'var(--surface)',
										border: '1px solid var(--line)',
										borderRadius: 'var(--r-lg)',
									}}
								>
									{emptyPanel()}
								</Box>
							) : view === 'list' ? (
								/* The table is the one white surface: its own border, rounded. */
								<Box
									sx={{
										'& .tblwrap': {
											bgcolor: 'var(--surface)',
											borderRadius: 'var(--r-lg)',
										},
									}}
								>
									<DataTable
										rows={rows}
										columns={columns}
										rowKey={(file) => file.pkFileId}
										onRowClick={openFile}
										selection={{
											selected: ticked,
											onChange: setTicked,
										}}
										caption={`Documents — ${title}`}
										fullScreen={full}
										loading={loadingSet}
										initialSort={{
											key: 'name',
											direction: 'asc',
										}}
										empty="No Files match these filters."
									/>
								</Box>
							) : rows.length === 0 ? (
								<Typography
									sx={{
										p: 'var(--s6)',
										textAlign: 'center',
										color: 'var(--ink-3)',
										bgcolor: 'var(--surface)',
										border: '1px solid var(--line)',
										borderRadius: 'var(--r-lg)',
									}}
								>
									No documents match these filters.
								</Typography>
							) : (
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns:
											'repeat(auto-fill, minmax(230px, 1fr))',
										gap: 'var(--s3)',
									}}
								>
									{rows.map((file) => {
										const on = ticked.has(file.pkFileId);
										return (
											<Box
												key={file.pkFileId}
												onClick={() => openFile(file)}
												/* Every card the same height, whatever the file name:
                         three fixed rows, the name on one line. */
												sx={{
													display: 'flex',
													flexDirection: 'column',
													gap: '12px',
													p: '14px 16px',
													minWidth: 0,
													bgcolor: on
														? 'var(--accent-surface)'
														: 'var(--surface)',
													border: '1px solid',
													borderColor: on
														? 'var(--accent-border)'
														: 'var(--line)',
													borderRadius: 'var(--r-lg)',
													cursor: 'pointer',
													'&:hover': {
														borderColor:
															'var(--accent-border)',
														boxShadow: 'var(--e2)',
													},
												}}
											>
												<Box
													sx={{
														display: 'flex',
														alignItems: 'center',
														gap: '8px',
														minWidth: 0,
													}}
												>
													<InsertDriveFileOutlinedIcon
														sx={{
															color: kindLook(
																file,
															).colour,
															fontSize: 22,
														}}
													/>
													<Typography
														title={
															file.originalName
														}
														sx={{
															flex: 1,
															minWidth: 0,
															fontSize:
																'var(--fs-body)',
															fontWeight: 600,
															lineHeight: '22px',
															/* One line, then an ellipsis; the full name is
                               in the tooltip. */
															whiteSpace:
																'nowrap',
															overflow: 'hidden',
															textOverflow:
																'ellipsis',
														}}
													>
														{file.originalName}
													</Typography>
													<Checkbox
														size="small"
														checked={on}
														inputProps={{
															'aria-label': `Select ${file.originalName}`,
														}}
														onClick={(event) =>
															event.stopPropagation()
														}
														onChange={() =>
															setTicked(
																(current) => {
																	const next =
																		new Set(
																			current,
																		);
																	if (
																		next.has(
																			file.pkFileId,
																		)
																	)
																		next.delete(
																			file.pkFileId,
																		);
																	else
																		next.add(
																			file.pkFileId,
																		);
																	return next;
																},
															)
														}
														sx={{ p: '2px' }}
													/>
												</Box>
												<Box
													sx={{
														fontSize:
															'var(--fs-small)',
														display: 'flex',
														alignItems: 'center',
														justifyContent:
															'space-between',
														gap: 'var(--s2)',
													}}
												>
													<KindLabel file={file} />
													<StatusBadge
														group="fileStatus"
														value={file.status}
													/>
												</Box>
												<Box
													sx={{
														mt: 'auto',
														display: 'flex',
														alignItems: 'center',
														justifyContent:
															'space-between',
														fontSize:
															'var(--fs-small)',
														color: 'var(--ink-3)',
													}}
												>
													<Typography
														component="span"
														sx={{
															fontSize: 'inherit',
														}}
													>
														{file.rowCount !== null
															? `${count(file.rowCount)} rows`
															: 'Not read yet'}{' '}
														·{' '}
														{bytes(file.sizeBytes)}
													</Typography>
													<Box
														sx={{
															display:
																'inline-flex',
														}}
														onClick={(event) =>
															event.stopPropagation()
														}
													>
														<DownloadIcon
															file={file}
														/>
														{rowMenu(file)}
													</Box>
												</Box>
											</Box>
										);
									})}
								</Box>
							)}
						</>
					)}
				</Box>

				<NameDialog
					open={dialog?.kind === 'new'}
					title="New group"
					initial=""
					confirmLabel="Create group"
					pending={create.isPending}
					error={create.error}
					taken={(name) => duplicateGroupName(name, groupList)}
					onClose={() => setDialog(null)}
					onSubmit={(name) =>
						create.mutate(name, {
							onSuccess: (created) => {
								setDialog(null);
								toast('ok', `Created ${name}`);
								const id = (
									created as
										{ pkFileGroupId?: string } | undefined
								)?.pkFileGroupId;
								if (id) open({ kind: 'group', id });
							},
						})
					}
				/>

				{currentGroup && (
					<>
						<NameDialog
							open={dialog?.kind === 'rename'}
							title="Rename group"
							initial={currentGroup.name}
							confirmLabel="Save"
							pending={rename.isPending}
							error={rename.error}
							taken={(name) =>
								duplicateGroupName(
									name,
									groupList,
									currentGroup.pkFileGroupId,
								)
							}
							onClose={() => setDialog(null)}
							onSubmit={(name) =>
								rename.mutate(
									{
										groupId: currentGroup.pkFileGroupId,
										name,
									},
									{
										onSuccess: () => {
											setDialog(null);
											toast('ok', `Renamed to ${name}`);
										},
									},
								)
							}
						/>
						<ConfirmDialog
							open={dialog?.kind === 'delete'}
							onClose={() => setDialog(null)}
							danger
							title={`Delete ${currentGroup.name}?`}
							question={`Only the group is removed. Its ${documents(currentGroup.fileCount)} stay in All fukess and in any other group.`}
							confirmLabel="Delete group"
							pending={remove.isPending}
							error={remove.error}
							onConfirm={() =>
								remove.mutate(currentGroup.pkFileGroupId, {
									onSuccess: () => {
										setDialog(null);
										toast(
											'ok',
											`Deleted ${currentGroup.name}`,
										);
										open({ kind: 'all' });
									},
								})
							}
						/>
						<AddFilesDialog
							open={dialog?.kind === 'add'}
							files={all.filter(
								(file) =>
									!(
										members
											.get(currentGroup.pkFileGroupId)
											?.has(file.pkFileId) ?? false
									),
							)}
							pending={actions.busy}
							error={null}
							onClose={() => setDialog(null)}
							onAdd={(picked) =>
								void actions
									.add(picked, currentGroup.pkFileGroupId)
									.then((ok) => ok && setDialog(null))
							}
						/>
					</>
				)}

				<ConfirmDialog
					open={dialog?.kind === 'remove'}
					onClose={() => setDialog(null)}
					title={`Remove ${documents(removeIds.current.length)} from ${currentGroup?.name ?? 'the group'}?`}
					question="They are not deleted. They stay in All files and in any other group they are in."
					confirmLabel="Remove from group"
					pending={actions.busy}
					onConfirm={() => {
						if (dialog?.kind !== 'remove' || !currentGroupId)
							return;
						void actions
							.remove(removeIds.current, currentGroupId)
							.then((ok) => {
								if (ok) {
									setDialog(null);
									setTicked(new Set());
								}
							});
					}}
				/>
			</>

			<ConfirmDialog
				open={deleteTarget !== null}
				onClose={() => {
					setDeleteTarget(null);
					deleteFile.reset();
				}}
				danger
				title={`Delete ${deleteTarget?.originalName ?? 'this file'}?`}
				question="It will no longer appear in Data files, file groups or new audits. Audits that already used it keep their data."
				confirmLabel="Delete file"
				pending={deleteFile.isPending}
				error={deleteFile.error}
				onConfirm={() => {
					if (!deleteTarget) return;
					const name = deleteTarget.originalName;
					deleteFile.mutate(deleteTarget.pkFileId, {
						onSuccess: () => {
							setDeleteTarget(null);
							toast('ok', `${name} deleted.`);
						},
					});
				}}
			/>
		</Box>
	);
}
